import api from '@/client/axiosInstance';

export type LegalDocumentType = 'terms' | 'privacy';
export type LegalDocumentAudience = 'consultant' | 'franchisor' | 'all';
export type LegalDocumentStatus = 'draft' | 'published' | 'archived';

export interface PublishedSummary {
  id: string;
  version: number | null;
  publishedAt: string | null;
  republishedAt: string | null;
}

export interface ChannelOverview {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  published: PublishedSummary | null;
  hasDraft: boolean;
  draftUpdatedAt: string | null;
}

export interface LegalOverview {
  channels: ChannelOverview[];
}

export interface ChannelDetail {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  published: {
    id: string;
    version: number | null;
    contentHtml: string;
    publishedAt: string | null;
  } | null;
  draft: {
    id: string;
    contentHtml: string;
    updatedAt: string;
  } | null;
}

export interface VersionSummary {
  id: string;
  version: number | null;
  status: LegalDocumentStatus;
  publishedAt: string | null;
  republishedAt: string | null;
}

export interface VersionDetail extends VersionSummary {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  contentHtml: string;
}

export interface SaveDraftResult {
  id: string;
  updatedAt: string;
}

export interface PublishResult {
  id: string;
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  version: number;
  publishedAt: string;
  republishedPrivacy: { id: string; version: number | null } | null;
}

// TCX-668: the current published version of a channel, served publicly for the
// Privacy / Terms pages and the re-acceptance modal's "Read …" links.
export interface PublicLegalDocument {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  version: number;
  contentHtml: string;
  publishedAt: string | null;
  republishedAt: string | null;
}

export interface AcceptLegalResult {
  accepted: Array<{
    documentType: LegalDocumentType;
    audience: LegalDocumentAudience;
    version: number;
  }>;
}

// TCX-669: one row in the admin Legal Acceptance History modal. An 'accepted'
// row is a real acceptance event; a 'pending' row is the current published
// version that hasn't been accepted yet (the Figma "N/A" row).
export interface LegalAcceptanceHistoryRow {
  documentType: LegalDocumentType;
  audience: LegalDocumentAudience;
  version: number;
  acceptedAt: string | null;
  userName: string | null; // populated for the franchise modal's "User" column
  ipAddress: string | null;
  status: 'accepted' | 'pending';
  acceptanceId: string | null; // null for 'pending'; used for the PDF download
}

// The two modals (Terms & Conditions, Privacy Policy) for one profile.
export interface LegalAcceptanceHistory {
  terms: LegalAcceptanceHistoryRow[];
  privacy: LegalAcceptanceHistoryRow[];
}

const base = 'legal-documents/admin';

export const getLegalOverview = async (): Promise<LegalOverview> => {
  const response = await api.get(`${base}/overview`);
  return response.data;
};

export const getLegalChannel = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
): Promise<ChannelDetail> => {
  const response = await api.get(`${base}/channels/${documentType}/${audience}`);
  return response.data;
};

export const getLegalHistory = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
): Promise<VersionSummary[]> => {
  const response = await api.get(
    `${base}/channels/${documentType}/${audience}/history`,
  );
  return response.data;
};

export const getLegalVersion = async (
  id: string,
): Promise<VersionDetail> => {
  const response = await api.get(`${base}/versions/${id}`);
  return response.data;
};

export const saveLegalDraft = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
  contentHtml: string,
): Promise<SaveDraftResult> => {
  const response = await api.put(`${base}/drafts/${documentType}/${audience}`, {
    contentHtml,
  });
  return response.data;
};

export const discardLegalDraft = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
): Promise<{ success: boolean }> => {
  const response = await api.delete(
    `${base}/drafts/${documentType}/${audience}`,
  );
  return response.data;
};

export const publishLegalDraft = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
): Promise<PublishResult> => {
  const response = await api.post(
    `${base}/drafts/${documentType}/${audience}/publish`,
  );
  return response.data;
};

// ---- acceptance audit trail (TCX-669) -------------------------------------

export const getUserLegalAcceptances = async (
  userId: string,
): Promise<LegalAcceptanceHistory> => {
  const response = await api.get(`${base}/acceptances/user/${userId}`);
  return response.data;
};

export const getFranchiseLegalAcceptances = async (
  franchiseId: string,
): Promise<LegalAcceptanceHistory> => {
  const response = await api.get(
    `${base}/acceptances/franchise/${franchiseId}`,
  );
  return response.data;
};

// PDF download of one acceptance (original accepted version + appended
// acceptance-record page), returned base64 to match the admin download
// convention. NOTE: the backend endpoint lands in B5 — this is inert until then.
export const downloadAcceptedVersion = async (
  acceptanceId: string,
): Promise<{ file: string }> => {
  const response = await api.get(
    `${base}/acceptances/${acceptanceId}/download`,
  );
  return response.data;
};

// ---- public + re-acceptance (TCX-668) -------------------------------------

export const getPublicLegalDocument = async (
  documentType: LegalDocumentType,
  audience: LegalDocumentAudience,
): Promise<PublicLegalDocument> => {
  const response = await api.get(
    `legal-documents/public/${documentType}/${audience}`,
  );
  return response.data;
};

export const acceptLegalDocuments = async (): Promise<AcceptLegalResult> => {
  const response = await api.post('legal-documents/accept');
  return response.data;
};
